import { useState, useEffect, useMemo, useRef } from "react";
import { Box, Paper, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Pagination, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, useMediaQuery, Chip, Stack, TextField, InputAdornment, Avatar, TableSortLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import LogoSpinner from '../../components/LogoSpinner/LogoSpinner';
import EventsApi from "../../api/eventsApi/eventsApi";
import { useNavigate } from "react-router-dom";
import { formatSectorDisplay } from "./facilitySectors";
import { useFitItemsPerPage, type FitItemsLayout } from "../../hooks/useFitItemsPerPage";


interface Pin {
    id: string;
    name: string;
    sector: string;
    sectorCustom?: string | null;
    latitude: number;
    longitude: number;
    radiusInMeters: number;
    createdDate: string;
    updatedDate: string;
    userFirstName: string;
    userLastName: string;
    fileAttachments?: { id: string; fileName: string }[];
}

type DateSortKey = 'created' | 'updated';

const Reports = () => {
    const navigate = useNavigate();
    // Responsiveness: tablet / mobil ayrımı için sade media query kullanımı
    const isTablet = useMediaQuery("(min-width:600px) and (max-width:1199.95px)");
    const isMobile = useMediaQuery("(max-width:599.95px)");
    const isCompactDesktop = useMediaQuery("(min-width:1200px) and (max-width:1439.95px)");
    const [isLoading, setIsLoading] = useState(true);
    const [pins, setPins] = useState<Pin[]>([]);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [orderBy, setOrderBy] = useState<DateSortKey>('created');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    const useCardLayout = isTablet || isMobile || isCompactDesktop;
    const [canDeletePins, setCanDeletePins] = useState(false);
    const deleteDisabledTooltip = 'Tesis kaldırma işlemi sadece Admin yetkilerine sahip kişilerce yapılabilir. Organizasyon yöneticinize danışınız.';
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [pinToDelete, setPinToDelete] = useState<Pin | null>(null);
    const [coverPhotos, setCoverPhotos] = useState<Record<string, string>>({});
    const [pendingSummary, setPendingSummary] = useState<{ totalPendingReports: number; pendingCountByPinId: Record<string, number> } | null>(null);
    const [filterPendingOnly, setFilterPendingOnly] = useState(false);

    const fetchPins = async () => {
        try {
            const user = await EventsApi.getUser();
            const [response, summary] = await Promise.all([
                EventsApi.getPins(),
                EventsApi.getPendingReportsSummary(user.organizationId).catch(() => ({
                    totalPendingReports: 0,
                    pendingCountByPinId: {} as Record<string, number>,
                })),
            ]);
            if (response && Array.isArray(response)) {
                setPins(response);
            }
            setPendingSummary(summary);
            setIsLoading(false);
        } catch (error) {
            console.error("Pin verisi alınırken hata:", error);
            setPendingSummary({ totalPendingReports: 0, pendingCountByPinId: {} });
            setIsLoading(false);
        }
    };

    const refreshPendingSummary = async () => {
        try {
            const user = await EventsApi.getUser();
            const summary = await EventsApi.getPendingReportsSummary(user.organizationId);
            setPendingSummary(summary);
        } catch {
            /* ignore */
        }
    };

    const handlePendingSummaryClick = () => {
        if (filterPendingOnly) {
            setFilterPendingOnly(false);
        } else if (pendingSummary && pendingSummary.totalPendingReports > 0) {
            setFilterPendingOnly(true);
            setPage(1);
        }
    };

    const handlePinClick = (pin: Pin) => {
        navigate(`/reports/${pin.id}`);
    };

    const handleDateSort = (key: DateSortKey) => {
        if (orderBy === key) {
            setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(key);
            setOrder('desc');
        }
        setPage(1);
    };

    // PinListItem component - masaüstü için tablo satırı
    const PinListItem = ({ pin, onPinClick }: { pin: Pin, onPinClick: (pin: Pin) => void }) => {
        const coverUrl = coverPhotos[pin.id];
        return (
            <TableRow
                onClick={() => onPinClick(pin)}
                sx={{
                    '&:hover': { backgroundColor: 'action.hover' },
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:nth-of-type(even)': { backgroundColor: 'grey.50' },
                }}
            >
                <TableCell sx={{ width: 56, px: 1 }}>
                    <Avatar
                        src={coverUrl}
                        variant="rounded"
                        sx={{
                            width: 40, height: 40,
                            bgcolor: coverUrl ? 'transparent' : '#094353',
                            fontSize: 16, fontWeight: 700,
                        }}
                    >
                        {!coverUrl && (pin.name?.[0] ?? <BusinessIcon />)}
                    </Avatar>
                </TableCell>
                <TableCell sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {pin.name}
                </TableCell>
                <TableCell sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatSectorDisplay(pin.sector, pin.sectorCustom)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(pin.createdDate).toLocaleString('tr-TR')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(pin.updatedDate).toLocaleString('tr-TR')}</TableCell>
                <TableCell sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {`${pin.userFirstName} ${pin.userLastName}`}
                </TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                        title={canDeletePins ? 'Tesisi sil' : deleteDisabledTooltip}
                        placement="top"
                    >
                        {/* Tooltip doesn't show on disabled elements unless wrapped */}
                        <span>
                            <IconButton
                                size="small"
                                disabled={!canDeletePins}
                                onClick={() => {
                                    if (!canDeletePins) return;
                                    setPinToDelete(pin);
                                    setDeleteDialogOpen(true);
                                }}
                                sx={{ color: canDeletePins ? '#b71c1c' : 'rgba(0,0,0,0.26)' }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </TableCell>
            </TableRow>
        );
    };

    // Kart görünümü - tablet / mobil / dar masaüstü için
    const PinCard = ({ pin, onPinClick }: { pin: Pin, onPinClick: (pin: Pin) => void }) => {
        const created = new Date(pin.createdDate).toLocaleString('tr-TR');
        const updated = new Date(pin.updatedDate).toLocaleString('tr-TR');

        return (
            <Paper
                data-fit-list-item
                elevation={0}
                onClick={() => onPinClick(pin)}
                sx={{
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: '#C8DDE4',
                    bgcolor: 'white',
                    px: 2,
                    py: 1.5,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: 'pointer',
                    '&:active': {
                        borderColor: 'primary.main',
                        transform: 'scale(0.99)',
                    },
                }}
            >
                <Avatar
                    src={coverPhotos[pin.id]}
                    variant="rounded"
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1,
                        bgcolor: coverPhotos[pin.id] ? 'transparent' : 'rgba(13,61,74,0.08)',
                        color: '#0D3D4A',
                        fontWeight: 700,
                        fontSize: 18,
                        flexShrink: 0,
                    }}
                >
                    {!coverPhotos[pin.id] && (pin.name?.[0] ?? 'T')}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            mb: 0.5,
                            color: '#0D3D4A',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {pin.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5A7A84' }}>
                        {formatSectorDisplay(pin.sector, pin.sectorCustom)}
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 0.5, flexWrap: 'wrap' }}
                    >
                        <Chip
                            size="small"
                            label={`Oluşturma: ${created}`}
                            sx={{ fontSize: 11, bgcolor: '#F2F7F9', color: '#0D3D4A' }}
                        />
                        <Chip
                            size="small"
                            label={`Güncelleme: ${updated}`}
                            sx={{ fontSize: 11, bgcolor: '#E4EFF3', color: '#0D3D4A' }}
                        />
                    </Stack>
                    <Typography
                        variant="caption"
                        sx={{ mt: 0.75, display: 'block', color: '#5A7A84' }}
                    >
                        Son güncelleyen: {pin.userFirstName} {pin.userLastName}
                    </Typography>
                </Box>
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!canDeletePins) return;
                        setPinToDelete(pin);
                        setDeleteDialogOpen(true);
                    }}
                    sx={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Tooltip
                        title={canDeletePins ? 'Tesisi sil' : deleteDisabledTooltip}
                        placement="left"
                    >
                        <span>
                            <IconButton
                                size="small"
                                disabled={!canDeletePins}
                                sx={{
                                    color: canDeletePins ? '#b71c1c' : 'rgba(0,0,0,0.26)',
                                }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Paper>
        );
    };

    useEffect(() => {
        fetchPins();
    }, []);

    // Fetch cover photos for all pins
    useEffect(() => {
        if (!pins.length) return;

        const fetchCovers = async () => {
            const newPhotos: Record<string, string> = {};
            const promises = pins.map(async (pin) => {
                const attachments = pin.fileAttachments ?? [];
                const cover = attachments.find((f) => {
                    const fn = (f.fileName ?? '').toLowerCase();
                    return fn.startsWith('cover') && (fn.endsWith('.jpg') || fn.endsWith('.jpeg') || fn.endsWith('.png'));
                });
                if (cover) {
                    try {
                        const blob = await EventsApi.getFileAttachment(cover.id);
                        newPhotos[pin.id] = URL.createObjectURL(blob);
                    } catch { /* ignore */ }
                }
            });
            await Promise.all(promises);
            if (Object.keys(newPhotos).length > 0) {
                setCoverPhotos(prev => ({ ...prev, ...newPhotos }));
            }
        };
        fetchCovers();

        return () => {
            // Cleanup object URLs on unmount
            Object.values(coverPhotos).forEach(url => URL.revokeObjectURL(url));
        };
    }, [pins]);



    useEffect(() => {
        const checkDeleteAccess = async () => {
            try {
                const user = await EventsApi.getUser();
                const superadminClaimId = '00000000-0000-0000-0000-000000000001';
                const adminClaimId = '00000000-0000-0000-0000-000000000002';
                setCanDeletePins(
                    user.operationClaimIds?.includes(superadminClaimId) ||
                    user.operationClaimIds?.includes(adminClaimId) ||
                    false
                );
            } catch (e) {
                console.error('Error checking pin delete access:', e);
                setCanDeletePins(false);
            }
        };
        checkDeleteAccess();
    }, []);

    const handleConfirmDelete = async () => {
        if (!pinToDelete) return;
        try {
            await EventsApi.deletePin(pinToDelete.id);
            setPins((prev) => prev.filter((p) => p.id !== pinToDelete.id));

            // keep pagination in range
            const nextCount = pins.length - 1;
            const nextTotalPages = Math.max(1, Math.ceil(nextCount / itemsPerPage));
            if (page > nextTotalPages) setPage(nextTotalPages);
            void refreshPendingSummary();
        } catch (e) {
            console.error('Pin delete error:', e);
            alert('Tesis silinemedi. Yetkiniz olmayabilir.');
        } finally {
            setDeleteDialogOpen(false);
            setPinToDelete(null);
        }
    };

    const filteredPins = useMemo(() => {
        let list = pins.filter(pin => {
            const term = searchTerm.toLowerCase();
            const sectorLabel = formatSectorDisplay(pin.sector, pin.sectorCustom).toLowerCase();
            return pin.name.toLowerCase().includes(term) || sectorLabel.includes(term);
        });
        if (filterPendingOnly && pendingSummary) {
            list = list.filter((pin) => (pendingSummary.pendingCountByPinId[pin.id] ?? 0) > 0);
        }
        return list;
    }, [pins, searchTerm, filterPendingOnly, pendingSummary]);

    const sortedFilteredPins = useMemo(() => {
        const next = [...filteredPins];
        next.sort((a, b) => {
            const dateA = new Date(orderBy === 'created' ? a.createdDate : a.updatedDate).getTime();
            const dateB = new Date(orderBy === 'created' ? b.createdDate : b.updatedDate).getTime();
            const cmp = dateA - dateB;
            return order === 'asc' ? cmp : -cmp;
        });
        return next;
    }, [filteredPins, orderBy, order]);

    const listMeasureRef = useRef<HTMLDivElement | null>(null);
    const listLayout: FitItemsLayout = useCardLayout ? 'list' : 'table';
    const listMeasureKey =
        sortedFilteredPins.length === 0 ? 'empty' : useCardLayout ? 'card' : 'table';
    const measuredItemsPerPage = useFitItemsPerPage(listMeasureRef, listLayout, !isLoading, undefined, listMeasureKey);
    // Tablet/mobilde kart listesi auto-height olduğu için ölçüme dayalı hesap ilk kartta 1'e kilitlenebiliyor.
    const itemsPerPage = isMobile ? 4 : isTablet ? 6 : measuredItemsPerPage;

    // Pagination hesaplamaları
    const totalPages = Math.max(1, Math.ceil(sortedFilteredPins.length / itemsPerPage));
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedPins = sortedFilteredPins.slice(startIndex, endIndex);

    useEffect(() => {
        const tp = Math.max(1, Math.ceil(sortedFilteredPins.length / itemsPerPage));
        setPage((p) => (p > tp ? tp : p));
    }, [itemsPerPage, sortedFilteredPins.length]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setPage(1); // arama değiştiğinde ilk sayfaya dön
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        // Sayfa değiştiğinde tablonun üstüne scroll et
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (isLoading) {
        return (
            <LogoSpinner size={60} minHeight="100vh" />
        );
    }

    return (
        <Box
            sx={{
                flexGrow: 1,
                width: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'center',
                py: isMobile ? 2 : (isTablet ? 4 : 3),
                px: isMobile ? 1.5 : (isTablet ? 3 : 4),
                height: isMobile || isTablet ? 'auto' : 'calc(100vh - 85px)',
                minHeight: isMobile || isTablet ? 'calc(100vh - 80px)' : 'auto',
                bgcolor: '#EDF4F6',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    // Tablet: maxWidth 820px ortalanmış, Masaüstü: tam genişlik
                    ...(isTablet ? { maxWidth: 820 } : (!isMobile ? { maxWidth: '100%' } : {})),
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        height: isMobile || isTablet ? 'auto' : '100%',
                        px: isMobile ? 2 : (isTablet ? 3 : 4),
                        py: isMobile ? 2.5 : 3,
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#F2F7F9',
                        border: '1px solid #C8DDE4',
                        boxShadow: 'none',
                    }}
                >
                    <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-end', gap: 2 }}>
                        <Box>
                            <Typography
                                variant={isMobile ? "overline" : "subtitle2"}
                                sx={{ color: '#5A7A84', letterSpacing: 2 }}
                            >
                                AEGIS SAHA DENETİM
                            </Typography>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{ fontWeight: 800, mt: 0.5, color: '#0D3D4A' }}
                            >
                                Tesislerim
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#5A7A84', mt: 0.5 }}
                            >
                                Kayıtlı tesisleriniz ve denetim geçmişleri
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1, gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#0D3D4A', fontWeight: 600 }}
                                >
                                    Güncel {pins.length} tesis
                                    {searchTerm.trim() ? (
                                        <Box component="span" sx={{ color: '#5A7A84', fontWeight: 500 }}>
                                            {' '}
                                            · Arama sonucu: {sortedFilteredPins.length}
                                        </Box>
                                    ) : null}
                                    {filterPendingOnly ? (
                                        <Box component="span" sx={{ color: '#5A7A84', fontWeight: 500 }}>
                                            {' '}
                                            · Yalnızca onay bekleyen raporu olan tesisler
                                        </Box>
                                    ) : null}
                                </Typography>
                                {pendingSummary !== null && (
                                    <Tooltip
                                        title={
                                            filterPendingOnly
                                                ? 'Tüm tesisleri göster'
                                                : 'Yalnızca onay bekleyen raporu olan tesisleri listele'
                                        }
                                    >
                                        <Chip
                                            size="small"
                                            label={`Onay bekleyen: ${pendingSummary.totalPendingReports} rapor`}
                                            onClick={handlePendingSummaryClick}
                                            disabled={!filterPendingOnly && pendingSummary.totalPendingReports === 0}
                                            sx={{
                                                fontWeight: 600,
                                                borderColor: '#C8DDE4',
                                                boxShadow: 'none',
                                                ...(filterPendingOnly
                                                    ? {
                                                        bgcolor: '#0D3D4A',
                                                        color: '#fff',
                                                        borderColor: '#0D3D4A',
                                                        '& .MuiChip-label': { color: '#fff' },
                                                        '&:hover': { bgcolor: '#134F60', borderColor: '#134F60' },
                                                    }
                                                    : {
                                                        bgcolor: '#fff',
                                                        color: '#0D3D4A',
                                                        '&:hover': { bgcolor: '#F2F7F9' },
                                                    }),
                                            }}
                                            variant={filterPendingOnly ? 'filled' : 'outlined'}
                                        />
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>

                        {/* Search Bar */}
                        <TextField
                            size="small"
                            placeholder="Tesis veya sektör ara..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            sx={{ minWidth: isMobile ? '100%' : 300 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#5A7A84' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    bgcolor: 'white',
                                    borderRadius: 1,
                                    '& fieldset': { borderColor: '#C8DDE4' },
                                    '&:hover fieldset': { borderColor: '#1A6070' },
                                }
                            }}
                        />
                    </Box>

                    {/* Content Area - Flex 1 */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        {useCardLayout && sortedFilteredPins.length > 0 && (
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}
                            >
                                <Typography variant="caption" sx={{ color: '#5A7A84', mr: 0.5 }}>
                                    Sıralama:
                                </Typography>
                                <Button
                                    size="small"
                                    variant={orderBy === 'created' ? 'contained' : 'outlined'}
                                    onClick={() => handleDateSort('created')}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: 12,
                                        minWidth: 0,
                                        py: 0.25,
                                        px: 1,
                                        boxShadow: 'none',
                                        ...(orderBy === 'created'
                                            ? {
                                                bgcolor: '#0D3D4A',
                                                color: '#fff',
                                                borderColor: '#0D3D4A',
                                                '&:hover': { bgcolor: '#134F60', color: '#fff', borderColor: '#134F60', boxShadow: 'none' },
                                            }
                                            : {
                                                borderColor: '#C8DDE4',
                                                color: '#0D3D4A',
                                                bgcolor: '#fff',
                                                '&:hover': { borderColor: '#1A6070', color: '#0D3D4A', bgcolor: '#fff', boxShadow: 'none' },
                                            }),
                                    }}
                                >
                                    Oluşturulma {orderBy === 'created' ? (order === 'asc' ? '↑' : '↓') : ''}
                                </Button>
                                <Button
                                    size="small"
                                    variant={orderBy === 'updated' ? 'contained' : 'outlined'}
                                    onClick={() => handleDateSort('updated')}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: 12,
                                        minWidth: 0,
                                        py: 0.25,
                                        px: 1,
                                        boxShadow: 'none',
                                        ...(orderBy === 'updated'
                                            ? {
                                                bgcolor: '#0D3D4A',
                                                color: '#fff',
                                                borderColor: '#0D3D4A',
                                                '&:hover': { bgcolor: '#134F60', color: '#fff', borderColor: '#134F60', boxShadow: 'none' },
                                            }
                                            : {
                                                borderColor: '#C8DDE4',
                                                color: '#0D3D4A',
                                                bgcolor: '#fff',
                                                '&:hover': { borderColor: '#1A6070', color: '#0D3D4A', bgcolor: '#fff', boxShadow: 'none' },
                                            }),
                                    }}
                                >
                                    Güncelleme {orderBy === 'updated' ? (order === 'asc' ? '↑' : '↓') : ''}
                                </Button>
                            </Stack>
                        )}
                        {paginatedPins.length > 0 ? (
                            useCardLayout ? (
                                <Box ref={listMeasureRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                    {paginatedPins.map((pin) => (
                                        <PinCard
                                            key={pin.id}
                                            pin={pin}
                                            onPinClick={handlePinClick}
                                        />
                                    ))}
                                </Box>
                            ) : (
                            <TableContainer
                                    ref={listMeasureRef}
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        width: '100%',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: '#C8DDE4',
                                        overflow: 'hidden',
                                        bgcolor: 'white',
                                    }}
                                >
                                    <Table stickyHeader size="medium" sx={{ tableLayout: 'fixed' }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, backgroundColor: '#0D3D4A', color: 'white', fontSize: '0.85rem', letterSpacing: 0.5, py: 1.5, width: 56, px: 1, borderRadius: 0 }}>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, backgroundColor: '#0D3D4A', color: 'white', fontSize: '0.85rem', letterSpacing: 0.5, py: 1.5, width: '24%', borderRadius: 0 }}>
                                                    Tesis Adı
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, backgroundColor: '#0D3D4A', color: 'white', fontSize: '0.85rem', letterSpacing: 0.5, py: 1.5, width: '16%', borderRadius: 0 }}>
                                                    Sektör
                                                </TableCell>
                                                <TableCell
                                                    sortDirection={orderBy === 'created' ? order : false}
                                                    sx={{ fontWeight: 700, backgroundColor: '#0D3D4A', color: 'white', fontSize: '0.85rem', letterSpacing: 0.5, py: 1.5, width: '16%', borderRadius: 0 }}
                                                >
                                                    <TableSortLabel
                                                        active={orderBy === 'created'}
                                                        direction={order}
                                                        onClick={() => handleDateSort('created')}
                                                        sx={{
                                                            color: 'inherit',
                                                            '&.MuiTableSortLabel-root': { color: 'rgba(255,255,255,0.98)' },
                                                            '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.9) !important' },
                                                        }}
                                                    >
                                                        Oluşturulma Tarihi
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell
                                                    sortDirection={orderBy === 'updated' ? order : false}
                                                    sx={{ fontWeight: 700, backgroundColor: '#0D3D4A', color: 'white', fontSize: '0.85rem', letterSpacing: 0.5, py: 1.5, width: '16%', borderRadius: 0 }}
                                                >
                                                    <TableSortLabel
                                                        active={orderBy === 'updated'}
                                                        direction={order}
                                                        onClick={() => handleDateSort('updated')}
                                                        sx={{
                                                            color: 'inherit',
                                                            '&.MuiTableSortLabel-root': { color: 'rgba(255,255,255,0.98)' },
                                                            '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.9) !important' },
                                                        }}
                                                    >
                                                        Güncelleme Tarihi
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, backgroundColor: '#0D3D4A', color: 'white', fontSize: '0.85rem', letterSpacing: 0.5, py: 1.5, width: '18%', borderRadius: 0 }}>
                                                    Son Güncelleyen
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, backgroundColor: '#0D3D4A', color: 'white', fontSize: '0.85rem', letterSpacing: 0.5, py: 1.5, width: 72, borderRadius: 0 }} align="center">
                                                    Sil
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedPins.map((pin) => (
                                                <PinListItem
                                                    key={pin.id}
                                                    pin={pin}
                                                    onPinClick={handlePinClick}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        ) : (
                            <Box
                                ref={listMeasureRef}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography color="text.secondary">
                                    {searchTerm ? 'Aramanıza uygun tesis bulunamadı' : 'Tesis bulunamadı'}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Pagination Footer */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto', pt: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size={isMobile ? "medium" : "large"}
                            showFirstButton
                            showLastButton
                            disabled={totalPages <= 1}
                        />
                    </Box>

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate('/reports/yeni-tesis')}
                        sx={{
                            mt: 2,
                            minHeight: 56,
                            borderRadius: 0,
                            fontWeight: 700,
                            fontSize: 17,
                            textTransform: 'none',
                            backgroundColor: '#0D3D4A !important',
                            color: '#fff !important',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#134F60 !important',
                                color: '#fff !important',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        + Yeni Tesis Ekle
                    </Button>
                </Paper>
            </Box>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setPinToDelete(null);
                }}
            >
                <DialogTitle>Tesis Sil</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 1 }}>
                        <b>{pinToDelete?.name}</b> tesisini silmek istediğinize emin misiniz?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setPinToDelete(null);
                        }}
                    >
                        Vazgeç
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDelete}
                        sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Reports; 